import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
  return JSON.stringify(d);
}

export function formatCurrency(n, code) {
  const c = (code || window.__wpm_currency || "USD").toUpperCase();
  const v = Number(n || 0);
  try {
    return v.toLocaleString(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 });
  } catch {
    return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  }
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

// Hours per time-unit used for cost calculations (standard 8h workday / 40h workweek)
export const UNIT_HOURS = { Minutes: 1/60, Hours: 1, Days: 8, Weeks: 40 };
// Same, but a Day counts as a full 24h and a Week as 168h — opt-in PER TASK
// via `extended_hours: true` so round-the-clock items (support rotas, incident
// response, hackathons) are costed and timelined against the right capacity.
export const UNIT_HOURS_EXTENDED = { Minutes: 1/60, Hours: 1, Days: 24, Weeks: 168 };

// Milliseconds of CALENDAR time per hour — used to convert a task's hoursPerUnit
// into actual Gantt bar width / schedule offset.
export const HOUR_MS = 3600 * 1000;

// Project-level time-unit → ms used ONLY for Gantt tick granularity.
export const UNIT_MS = {
  Minutes: 60 * 1000,
  Hours: HOUR_MS,
  Days: 24 * HOUR_MS,
  Weeks: 7 * 24 * HOUR_MS,
};

// Short duration-label suffix for Gantt bars.
export const UNIT_SUFFIX = { Minutes: "m", Hours: "h", Days: "d", Weeks: "w" };

// Hours accounted per time-unit FOR A SPECIFIC TASK, honoring its extended_hours flag.
export function taskHoursPerUnit(task, timeUnit = "Days") {
  const m = task && task.extended_hours ? UNIT_HOURS_EXTENDED : UNIT_HOURS;
  return m[timeUnit] || m.Days;
}

export function computePlan(tasks, roles, timeUnit = "Days") {
  const rateById = new Map(roles.map((r) => [r.id, Number(r.rate || 0)]));
  const byNum = new Map();

  const prepared = tasks.map((t) => {
    const est = Number(t.estimated_time || 0);
    const actual = Number(t.actual_time || 0);
    const forecast = Math.max(est, actual);
    const hoursPerUnit = taskHoursPerUnit(t, timeUnit);
    const rate = rateById.get(t.role_id) || 0;
    const estCost = est * hoursPerUnit * rate;
    const durationMs = forecast * hoursPerUnit * HOUR_MS;
    return { ...t, _estCost: estCost, _estDays: forecast, _hoursPerUnit: hoursPerUnit, _durationMs: durationMs };
  });

  prepared.forEach((t) => byNum.set(t.task_number, t));

  // Schedule start/end per task. Each task's duration AND its lag are scaled by
  // that task's own hoursPerUnit so a 1-Day extended task takes 24h and a
  // 1-Day lag before it also waits 24h — regardless of the project's time unit.
  const start = new Date();
  function schedule(t, visiting = new Set()) {
    if (t._planStart) return t;
    if (visiting.has(t.task_number)) return t;
    visiting.add(t.task_number);
    let s = new Date(start);
    if (t.order && byNum.has(Number(t.order))) {
      const pred = schedule(byNum.get(Number(t.order)), visiting);
      const predEnd = pred._planEnd || new Date(start);
      if (t.order_type === "Start With") {
        s = new Date(predEnd.getTime() - (pred._durationMs || 0));
      } else {
        s = new Date(predEnd);
      }
      const lag = Number(t.lag || 0);
      s = new Date(s.getTime() + lag * t._hoursPerUnit * HOUR_MS);
    }
    const e = new Date(s.getTime() + (t._durationMs || 0));
    t._planStart = s;
    t._planEnd = e;
    return t;
  }
  prepared.forEach((t) => schedule(t));

  const totalEstCost = prepared.reduce((a, t) => a + t._estCost, 0);
  const planEnd = prepared.reduce((max, t) => (t._planEnd && (!max || t._planEnd > max) ? t._planEnd : max), null);

  // Mark critical path: walk backwards from the task(s) ending at planEnd through predecessors
  const critical = new Set();
  if (planEnd) {
    const terminals = prepared.filter((t) => t._planEnd && Math.abs(t._planEnd.getTime() - planEnd.getTime()) < 1000);
    const walk = (t) => {
      if (!t || critical.has(t.task_number)) return;
      critical.add(t.task_number);
      if (t.order && byNum.has(Number(t.order))) walk(byNum.get(Number(t.order)));
    };
    terminals.forEach(walk);
  }
  prepared.forEach((t) => (t._critical = critical.has(t.task_number)));

  const totalActualCost = prepared.reduce((a, t) => a + Number(t.actual_cost || 0), 0);
  const actualCompletionDates = prepared.map((t) => t.actual_completion_date).filter(Boolean);
  const actualEnd = actualCompletionDates.length
    ? new Date(actualCompletionDates.sort().slice(-1)[0])
    : null;

  return {
    tasks: prepared,
    estimatedCost: totalEstCost,
    plannedDelivery: planEnd,
    actualCost: totalActualCost,
    actualDelivery: actualEnd,
    criticalPath: [...critical].sort((a, b) => a - b),
  };
}

export function variance(statedDue, calculatedDue) {
  if (!statedDue || !calculatedDue) return null;
  const s = new Date(statedDue);
  const c = new Date(calculatedDue);
  const diffDays = Math.round((c - s) / (24 * 3600 * 1000));
  return diffDays;
}

/** Build a baseline-diff map keyed by task_number: { timeDelta, costDelta, nameChanged } */
export function diffBaseline(currentTasks, baseline, roles, timeUnit = "Days") {
  if (!baseline || baseline.length === 0) return null;
  const unitHours = UNIT_HOURS[timeUnit] || 8;
  const rateById = new Map(roles.map((r) => [r.id, Number(r.rate || 0)]));
  const costOf = (t) => Number(t.estimated_time || 0) * unitHours * (rateById.get(t.role_id) || 0);
  const bByNum = new Map(baseline.map((t) => [t.task_number, t]));
  const result = {};
  currentTasks.forEach((t) => {
    const b = bByNum.get(t.task_number);
    if (!b) { result[t.task_number] = { isNew: true, timeDelta: 0, costDelta: 0 }; return; }
    result[t.task_number] = {
      timeDelta: Number(t.estimated_time || 0) - Number(b.estimated_time || 0),
      costDelta: costOf(t) - costOf(b),
      isNew: false,
    };
  });
  return result;
}
